import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import BookingRequestActionCreator from 'action_creators/booking_request';
import Dimmer from 'views/shared/ui/dimmer';

import AffiliatePaymentSelector from 'views/anyguide/affiliates/components/AffiliatePaymentSelector';
import { useCheckout } from './CheckoutContext';
import { createPageTwoValidationSchema } from './pageTwoValidation';
import type { PaymentFormData, CheckoutPageTwoProps } from './types';
import { useUpdatePaymentIntent } from './payment/paymentIntentsApi';

import { LanguageHeader, LanguageSelector } from '../default/header';
import { BookingTimer } from './booking_timer';
import { Header, PartnerHeader } from './header';
import { Summary } from './summary';
import { Payment, type PaymentHandle } from './payment';
import { Discount } from './discount';
import { MessageGuide } from './message-guide';
import { PrivacyPolicy } from './privacy_policy';
import { PoweredBy } from './powered-by';
import { PaymentStepHeader } from '../payment/PaymentStepHeader';
import {
  PageContainer,
  SummaryWrapper,
  ColumnContainer,
  ComponentContainer,
  ConfirmationContainer,
  ButtonContainer,
  SubmitButton,
  PaymentSubtext,
  ConfirmationSubtext,
} from './styles';

export const CheckoutPageTwo: React.FC<CheckoutPageTwoProps> = ({
  onSubmit,
  onBack,
}) => {
  const { formatMessage } = useIntl();
  const context = useCheckout();
  const {
    summaryProps,
    pricingData,
    tourId,
    bookingRequestId,
    isBookingInvoice,
    isLiveBooking,
    isAffiliate,
    isUnpaidBooking,
    isPayLaterEnabled,
    isOutsidePayLaterCutoff,
    units,
    partnerInfo,
    willExpireOn,
    i18nList,
    i18nLang,
    zeroPricedTour,
    fullyDiscounted,
    paidInCash,
    total,
    currency,
    stripeTheme,
    tourSettings,
    includesTransport,
    privacyPolicyLink,
    privacyPolicyLinkText,
    discountApplied,
    discountLoading,
    discountErrors,
    loading,
  } = context;

  const [selectedPaymentType, setSelectedPaymentType] = useState<string>(
    isPayLaterEnabled && isOutsidePayLaterCutoff ? 'pay-later' : 'charge-card'
  );
  const [isPayLaterSelected, setIsPayLaterSelected] = useState<boolean>(
    isPayLaterEnabled && isOutsidePayLaterCutoff
  );
  const [paymentIntentId, setPaymentIntentId] = useState<string | undefined>();
  const [paymentLoading, setPaymentLoading] = useState<boolean>(false);

  const { mutate: updatePaymentIntent } = useUpdatePaymentIntent();

  const paymentRef = useRef<PaymentHandle>(null);

  const shouldShowPayment = useMemo(() => {
    return zeroPricedTour !== null && !zeroPricedTour;
  }, [zeroPricedTour]);

  const shouldDisablePayment = useMemo(() => {
    return paidInCash;
  }, [paidInCash]);

  const shouldShowPayLater = useMemo(() => {
    return (
      zeroPricedTour !== null &&
      !zeroPricedTour &&
      isPayLaterEnabled &&
      !isUnpaidBooking
    );
  }, [zeroPricedTour, isPayLaterEnabled, isUnpaidBooking]);

  const shouldValidatePrivacyPolicy = useMemo(() => {
    const affiliatePayLaterCheckout = isAffiliate && isUnpaidBooking;
    return (
      privacyPolicyLink && privacyPolicyLinkText && !affiliatePayLaterCheckout
    );
  }, [isAffiliate, isUnpaidBooking, privacyPolicyLink, privacyPolicyLinkText]);

  const shouldDisableDiscount = useMemo(() => {
    return !shouldShowPayment || paidInCash || isPayLaterSelected;
  }, [shouldShowPayment, paidInCash, isPayLaterSelected]);

  const validationSchema = useMemo(
    () =>
      createPageTwoValidationSchema(formatMessage, {
        shouldValidatePrivacyPolicy: Boolean(shouldValidatePrivacyPolicy),
      }),
    [formatMessage, shouldValidatePrivacyPolicy]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<PaymentFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      privacy_policy_accepted: false,
      message: '',
      is_pay_later_selected: isPayLaterSelected,
    },
    mode: 'onBlur',
  });

  const isFormDisabled = loading || isSubmitting || paymentLoading;
  const paymentFormData = watch();

  const handleFormSubmit = useCallback(
    async (data: PaymentFormData) => {
      if (
        shouldShowPayment &&
        !shouldDisablePayment &&
        !isPayLaterSelected &&
        paymentRef.current
      ) {
        setPaymentLoading(true);
        paymentRef.current.submit();
      } else {
        onSubmit(data);
      }
    },
    [onSubmit, shouldShowPayment, shouldDisablePayment, isPayLaterSelected]
  );

  const handlePaymentTypeChange = useCallback(
    (paymentType: string) => {
      const isPayLater = paymentType === 'pay-later';
      setSelectedPaymentType(paymentType);
      setIsPayLaterSelected(isPayLater);
      setValue('is_pay_later_selected', isPayLater);
    },
    [setValue]
  );

  const handleDiscountApply = useCallback((params: any) => {
    BookingRequestActionCreator.applyDiscount(params);
  }, []);

  const handleDiscountRemove = useCallback((params: any) => {
    BookingRequestActionCreator.removeDiscount(params);
  }, []);

  useEffect(() => {
    if (
      !fullyDiscounted &&
      discountApplied &&
      paymentIntentId &&
      bookingRequestId
    ) {
      updatePaymentIntent({
        bookingRequestId,
        paymentIntentId,
        data: {
          amount_cents: Math.round(total * 100),
          currency: currency || 'usd',
        },
      });
    }
  }, [
    discountApplied,
    paymentIntentId,
    bookingRequestId,
    total,
    currency,
    updatePaymentIntent,
  ]);

  const renderPartnerHeader = () => {
    const affiliatePayLaterCheckout = isAffiliate && isUnpaidBooking;
    if ((!isBookingInvoice && !isLiveBooking) || affiliatePayLaterCheckout) {
      return null;
    }

    return (
      <PartnerHeader
        isLiveHeader={isLiveBooking}
        hideOnMobile
        bgColor={partnerInfo.brand_color}
        title={partnerInfo.business_name}
        logo={partnerInfo.logo}
      />
    );
  };

  const summaryPropsFormatted = useMemo(
    () => ({
      request: summaryProps.request as any,
      tour_id: tourId?.toString() || '',
      location: summaryProps.location || { label: '', value: '' },
      pricingData,
    }),
    [summaryProps, tourId, pricingData]
  );

  const paymentSectionStepNumber = useMemo(() => {
    const numOfGuests = units.reduce((a, e) => a + e.count, 0);
    const ignoreMaxGuest =
      context.guestOfGuestMaxGuests === 0
        ? true
        : numOfGuests <= context.guestOfGuestMaxGuests;
    const shouldCollectGuestInfo =
      !isAffiliate &&
      context.isGuestOfGuestEnabled &&
      numOfGuests > 1 &&
      ignoreMaxGuest;

    const hasQuestions =
      Array.isArray((summaryProps as any).checkout_questions) &&
      (summaryProps as any).checkout_questions.length > 0;
    const shouldShowQuestions = hasQuestions;
    const shouldShowWaiver =
      !isAffiliate && summaryProps.request.tour_waiver_info?.required;
    const shouldShowAdditionalInfo =
      !isAffiliate && (shouldShowQuestions || shouldShowWaiver);

    let stepCount = 0;

    if (context.isGuestInfoCollectionEnabled) {
      stepCount++;
    }

    if (shouldCollectGuestInfo && !isBookingInvoice) {
      stepCount++;
    }

    if (shouldShowAdditionalInfo) {
      stepCount++;
    }

    return String(stepCount + 1);
  }, [
    units,
    isBookingInvoice,
    isAffiliate,
    context.isGuestInfoCollectionEnabled,
    context.isGuestOfGuestEnabled,
    context.guestOfGuestMaxGuests,
    summaryProps,
  ]);

  const renderPaymentSubtext = () => {
    if (isPayLaterSelected) {
      return null;
    }

    if (summaryProps.request.payment_subtext) {
      return (
        <PaymentSubtext
          className="contains-brand-link payment-subtext form-details"
          dangerouslySetInnerHTML={{
            __html: summaryProps.request.payment_subtext,
          }}
        />
      );
    }
    return (
      <PaymentSubtext className="form-details">
        {formatMessage({
          defaultMessage:
            'We do not store your credit card information. All payments are encrypted and securely processed through our PCI compliant provider.',
        })}
      </PaymentSubtext>
    );
  };

  const renderSubmitButtonLabel = () => {
    const settings = tourSettings;
    const isInvoice = summaryProps.request.is_invoice;

    if (isInvoice) {
      return (
        settings?.submit_invoice_label?.value ||
        formatMessage({ defaultMessage: 'Submit' })
      );
    }

    if (isLiveBooking) {
      return formatMessage({ defaultMessage: 'Register' });
    }

    const label = settings?.submit_booking_label?.value;
    return label === 'Confirm Purchase'
      ? formatMessage({ defaultMessage: 'Confirm Purchase' })
      : label || formatMessage({ defaultMessage: 'Confirm Purchase' });
  };

  const checkoutPageCssClasses = classnames(
    'CheckoutPage CheckoutMobileFirstPage',
    {
      'is-live': isLiveBooking,
    }
  );

  const confirmationCssClasses = classnames('ConfirmationContainer', {
    'is-live': isLiveBooking,
  });

  const showBookingTimer = !isBookingInvoice && !isLiveBooking;

  return (
    <PageContainer className={checkoutPageCssClasses}>
      <Dimmer fixed transparent loader active={loading} />
      <ColumnContainer className="MobileFirstColumnContainer">
        {!isLiveBooking && (
          <SummaryWrapper>
            <ComponentContainer className="ComponentContainer MobileFirstSummaryColumn">
              <Summary {...summaryPropsFormatted} />
            </ComponentContainer>
          </SummaryWrapper>
        )}

        {renderPartnerHeader()}
        {isLiveBooking && i18nList.length > 0 && (
          <LanguageHeader>
            <LanguageSelector
              value={{ value: i18nLang, label: i18nLang }}
              options={i18nList.map((lang) => ({ value: lang, label: lang }))}
              onChange={() => {}}
            />
          </LanguageHeader>
        )}

        <ComponentContainer className="ComponentContainer MobileFirstColumn">
          {showBookingTimer && <Header />}
          {showBookingTimer && willExpireOn && (
            <BookingTimer
              sessionExpired={willExpireOn}
              tourId={tourId}
              guideId={summaryProps.request.guide_id}
              originUrl={summaryProps.request.origin_url}
            />
          )}

          {shouldShowPayment && !shouldDisablePayment && (
            <div>
              {!shouldDisableDiscount && (
                <Discount
                  additionalParams={{
                    tour_id: tourId,
                    id: bookingRequestId,
                    payment_intent_id: paymentIntentId,
                  }}
                  discountApplied={discountApplied}
                  errors={discountErrors}
                  discountLoading={discountLoading}
                  onApplyDiscount={handleDiscountApply}
                  onRemoveDiscount={handleDiscountRemove}
                />
              )}

              {!fullyDiscounted && (
                <div>
                  <PaymentStepHeader
                    stepNumber={paymentSectionStepNumber}
                    showPersonalSection={context.isGuestInfoCollectionEnabled}
                  />
                  {shouldShowPayLater && (
                    <AffiliatePaymentSelector
                      paymentType={selectedPaymentType}
                      paymentTypeChanged={handlePaymentTypeChange}
                      disabledOptions={
                        !isOutsidePayLaterCutoff ? ['pay-later'] : []
                      }
                    />
                  )}
                  {renderPaymentSubtext()}
                  {!isPayLaterSelected && (
                    <Payment
                      ref={paymentRef}
                      bookingRequestId={bookingRequestId}
                      totalPriceCents={Math.round(total * 100)}
                      currency={currency}
                      stripeTheme={stripeTheme}
                      onIntentConfirm={() => {
                        onSubmit({
                          ...paymentFormData,
                          payment_intent_id: paymentIntentId,
                        });
                      }}
                      onIntentCreated={(paymentIntentId) => {
                        setPaymentIntentId(paymentIntentId);
                      }}
                      onError={() => setPaymentLoading(false)}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </ComponentContainer>
      </ColumnContainer>

      {!isLiveBooking && !isAffiliate && (
        <ComponentContainer className="ComponentContainer">
          <Controller
            name="message"
            control={control}
            render={({ field }) => (
              <MessageGuide
                value={field.value}
                onChange={(value: string) => {
                  field.onChange(value);
                }}
                guideName={summaryProps.request.guide_full_name || ''}
                borderless={false}
                showHint={includesTransport}
              />
            )}
          />
        </ComponentContainer>
      )}

      <ConfirmationContainer className={confirmationCssClasses}>
        {shouldValidatePrivacyPolicy && (
          <Controller
            name="privacy_policy_accepted"
            control={control}
            render={({ field }) => (
              <PrivacyPolicy
                fields={{
                  get: () => field.value,
                }}
                errors={{
                  get: () => {
                    const error = errors.privacy_policy_accepted;
                    return error ? [error.message || ''] : undefined;
                  },
                }}
                onChange={(_fieldName: string, value: boolean) => {
                  field.onChange(value);
                }}
                privacy_policy_link={privacyPolicyLink}
                privacy_policy_link_text={privacyPolicyLinkText}
              />
            )}
          />
        )}
        <ButtonContainer>
          <SubmitButton
            type="button"
            className="ui button secondary"
            onClick={onBack}
            disabled={isFormDisabled}
          >
            {formatMessage({ defaultMessage: 'Back' })}
          </SubmitButton>
          <SubmitButton
            type="submit"
            color="primary"
            onClick={handleSubmit(handleFormSubmit)}
            className={classnames({
              loading: isFormDisabled,
              'brand-bg': true,
            })}
            disabled={isFormDisabled}
            data-testid="checkout-page-two-submit"
          >
            {renderSubmitButtonLabel()}
          </SubmitButton>
        </ButtonContainer>
        <ConfirmationSubtext className="hint confirmation-subtext">
          {summaryProps.request.confirmation_subtext}
        </ConfirmationSubtext>
        {!isFormDisabled && (
          <PoweredBy hidePolicyLinks={context.hidePolicyLinks} />
        )}
      </ConfirmationContainer>
    </PageContainer>
  );
};
